import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Trash2, Send, RefreshCw, Loader2, FileText, Eye, Pencil, Image, Video, File, MessageSquare, Phone, ExternalLink, Copy, Search, X, ChevronLeft, ChevronRight, Lock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import TemplateBuilder from '../components/templates/TemplateBuilder';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// WhatsApp Preview Component (compact version for view dialog)
const TemplatePreview = ({ template }) => {
  return (
    <div className="bg-[#E4DDD6] rounded-lg p-4">
      <div className="max-w-[280px] mx-auto">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Header */}
          {template.header_type && (
            <div className="border-b border-gray-100">
              {template.header_type === 'IMAGE' && (
                <div className="h-32 bg-gray-200 flex items-center justify-center overflow-hidden">
                  {template.header_preview ? (
                    <img src={template.header_preview} alt="Header" className="w-full h-full object-cover" />
                  ) : template.header_content?.startsWith('https://') ? (
                    <img src={template.header_content} alt="Header" className="w-full h-full object-cover" />
                  ) : template.header_content ? (
                    <div className="flex flex-col items-center text-gray-500 gap-1">
                      <Image className="w-8 h-8" />
                      <span className="text-[10px] font-medium">{template.header_filename || 'Image'}</span>
                      <span className="text-[9px] text-gray-400">Stored on Meta</span>
                    </div>
                  ) : (
                    <Image className="w-8 h-8 text-gray-400" />
                  )}
                </div>
              )}
              {template.header_type === 'VIDEO' && (
                <div className="h-32 bg-gray-800 flex items-center justify-center overflow-hidden relative">
                  {template.header_content?.startsWith('https://') ? (
                    <video src={template.header_content} className="w-full h-full object-cover" controls muted playsInline />
                  ) : template.header_preview ? (
                    <>
                      <img src={template.header_preview} alt="Video thumbnail" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center">
                          <Video className="w-4 h-4 text-gray-800" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center text-gray-400 gap-1">
                      <Video className="w-8 h-8" />
                      {template.header_content && <span className="text-[9px] text-gray-500">Stored on Meta</span>}
                    </div>
                  )}
                </div>
              )}
              {template.header_type === 'DOCUMENT' && (
                <div className="h-16 bg-gray-100 flex items-center px-3 gap-3">
                  <div className="w-10 h-10 bg-[#EB493D] rounded flex items-center justify-center flex-shrink-0">
                    <File className="w-5 h-5 text-white" />
                  </div>
                  {template.header_content?.startsWith('https://') ? (
                    <a href={template.header_content} target="_blank" rel="noopener noreferrer" className="text-[12px] text-[#00A884] truncate underline">
                      {template.header_filename || 'View Document'}
                    </a>
                  ) : (
                    <span className="text-[12px] text-gray-700 truncate">{template.header_filename || 'document.pdf'}</span>
                  )}
                </div>
              )}
              {template.header_type === 'TEXT' && template.header_content && (
                <div className="px-3 py-2">
                  <p className="text-[14px] font-semibold text-[#131517]">{template.header_content}</p>
                </div>
              )}
            </div>
          )}

          {/* Body */}
          <div className="px-3 py-2">
            <p className="text-[13px] text-[#131517] whitespace-pre-wrap">{template.body_text}</p>
          </div>

          {/* Footer */}
          {template.footer_text && (
            <div className="px-3 pb-2">
              <p className="text-[11px] text-[#8A8A8B]">{template.footer_text}</p>
            </div>
          )}

          {/* Buttons */}
          {template.buttons && template.buttons.length > 0 && (
            <div className="border-t border-gray-100">
              {template.buttons.map((btn, idx) => (
                <div key={idx} className="border-b border-gray-100 last:border-b-0 px-3 py-2 flex items-center justify-center gap-2">
                  {btn.type === 'PHONE_NUMBER' && <Phone className="w-3 h-3 text-[#00A884]" />}
                  {btn.type === 'URL' && <ExternalLink className="w-3 h-3 text-[#00A884]" />}
                  {btn.type === 'QUICK_REPLY' && <MessageSquare className="w-3 h-3 text-[#00A884]" />}
                  {btn.type === 'COPY_CODE' && <Copy className="w-3 h-3 text-[#00A884]" />}
                  <span className="text-[12px] text-[#00A884] font-medium">{btn.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');

  // Pagination
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  // Derived: filtered + paginated
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter(t => {
      const matchName = !q || t.name.toLowerCase().includes(q);
      const matchCategory = filterCategory === 'ALL' || t.category === filterCategory;
      const matchStatus = filterStatus === 'ALL' || t.status === filterStatus;
      return matchName && matchCategory && matchStatus;
    });
  }, [templates, search, filterCategory, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Reset to page 1 whenever filters/pageSize change
  useEffect(() => { setCurrentPage(1); }, [search, filterCategory, filterStatus, pageSize]);

  const clearFilters = () => { setSearch(''); setFilterCategory('ALL'); setFilterStatus('ALL'); };
  const hasFilters = search || filterCategory !== 'ALL' || filterStatus !== 'ALL';

  // Unique categories/statuses from loaded templates
  const categories = useMemo(() => {
    const s = new Set(templates.map(t => t.category).filter(Boolean));
    return [...s].sort();
  }, [templates]);

  const statuses = useMemo(() => {
    const s = new Set(templates.map(t => t.status).filter(Boolean));
    return [...s].sort();
  }, [templates]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/templates/sync`);
      const { templates: synced, summary } = response.data;
      setTemplates(synced);
      const { added, updated, total } = summary;
      if (total === 0) {
        toast.info('No templates found on WhatsApp Business account');
      } else {
        toast.success(
          `Synced ${total} template${total !== 1 ? 's' : ''} — ${added} new, ${updated} updated`,
          { duration: 5000 }
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync templates');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreate = async (formData) => {
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/templates`, formData);
      setTemplates([response.data, ...templates]);
      setIsCreateOpen(false);
      
      if (response.data.api_error) {
        toast.warning(`Template created locally. API error: ${response.data.api_error}`);
      } else {
        toast.success('Template created successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (templateId) => {
    try {
      const response = await axios.post(`${API_URL}/templates/${templateId}/submit`);
      setTemplates(templates.map(t => t.id === templateId ? response.data : t));
      toast.success('Template submitted to WhatsApp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API_URL}/templates/${templateId}`);
      setTemplates(templates.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  // Edit rules (Meta official):
  // DRAFT: always editable | PENDING: blocked | APPROVED: 1/24h | REJECTED/PAUSED: unlimited
  const canEdit = (template) => template.status !== 'PENDING';

  const getEditTooltip = (template) => {
    if (template.status === 'PENDING') return 'Cannot edit: pending Meta approval';
    if (template.status === 'APPROVED') return 'Edit (max 1 time per 24 hours for approved templates)';
    return 'Edit template';
  };

  const handleEdit = async (formData) => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/templates/${selectedTemplate.id}/edit`, {
        header_type: formData.header_type || null,
        header_content: formData.header_content || null,
        header_filename: formData.header_filename || null,
        header_preview: formData.header_preview || null,
        header_example: formData.header_example || null,
        body_text: formData.body_text,
        body_examples: formData.body_examples || [],
        footer_text: formData.footer_text || null,
        buttons: formData.buttons || [],
      });
      setTemplates(templates.map(t => t.id === selectedTemplate.id ? response.data : t));
      setIsEditOpen(false);
      setSelectedTemplate(null);
      if (response.data.api_error) {
        toast.warning(`Template updated locally. API error: ${response.data.api_error}`);
      } else {
        toast.success('Template updated successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      DRAFT: 'bg-[#EFEFEF] text-[#595C5C] border-[#D9E2E5]',
      PENDING: 'bg-[#F9BC34]/10 text-[#F9BC34] border-[#F9BC34]/20',
      APPROVED: 'bg-[#3BAB59]/10 text-[#3BAB59] border-[#3BAB59]/20',
      REJECTED: 'bg-[#EB493D]/10 text-[#EB493D] border-[#EB493D]/20',
      PAUSED: 'bg-[#8A8A8B]/10 text-[#8A8A8B] border-[#8A8A8B]/20',
    };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium border ${styles[status] || styles.DRAFT}`}>
        {status}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const styles = {
      UTILITY: 'bg-blue-50 text-blue-700 border-blue-200',
      MARKETING: 'bg-purple-50 text-purple-700 border-purple-200',
      AUTHENTICATION: 'bg-orange-50 text-orange-700 border-orange-200'
    };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium border ${styles[category] || ''}`}>
        {category}
      </span>
    );
  };

  const getHeaderIcon = (headerType) => {
    switch (headerType) {
      case 'IMAGE': return <Image className="w-3 h-3" />;
      case 'VIDEO': return <Video className="w-3 h-3" />;
      case 'DOCUMENT': return <File className="w-3 h-3" />;
      case 'TEXT': return <FileText className="w-3 h-3" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[30px] font-semibold text-[#131517]">Templates</h1>
          <p className="text-[14px] text-[#595C5C] mt-1">Manage your WhatsApp message templates</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={syncing}
            className="border-[#D9E2E5] text-[#595C5C] hover:bg-[#EFEFEF]"
            data-testid="sync-templates-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sync
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#131517] hover:bg-[#333537]" data-testid="create-template-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-[20px] font-semibold text-[#131517]">Create Template</DialogTitle>
              </DialogHeader>
              <div className="pt-4">
                <TemplateBuilder 
                  onSave={handleCreate}
                  onCancel={() => setIsCreateOpen(false)}
                  saving={saving}
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filter Bar */}
      <Card className="border-[#D9E2E5]">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8B]" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name…"
                className="pl-9 border-[#D9E2E5] h-9 text-[13px]"
                data-testid="search-input"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A8B] hover:text-[#595C5C]">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Category filter */}
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-44 h-9 text-[13px] border-[#D9E2E5]" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Status filter */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40 h-9 text-[13px] border-[#D9E2E5]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Statuses</SelectItem>
                {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Clear */}
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-[#8A8A8B] hover:text-[#595C5C] gap-1.5" data-testid="clear-filters-btn">
                <X className="w-3.5 h-3.5" /> Clear
              </Button>
            )}

            {/* Result count */}
            <span className="text-[12px] text-[#8A8A8B] ml-auto">
              {filtered.length} of {templates.length} template{templates.length !== 1 ? 's' : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Templates Table */}
      <Card className="border-[#D9E2E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#595C5C]" />
            </div>
          ) : paginated.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#D9E2E5] hover:bg-transparent">
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Name</TableHead>
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Category</TableHead>
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Status</TableHead>
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Header</TableHead>
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Buttons</TableHead>
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Language</TableHead>
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.map((template) => (
                    <TableRow key={template.id} className="border-[#D9E2E5]" data-testid={`template-row-${template.id}`}>
                      <TableCell className="font-medium text-[#131517] max-w-[220px]">
                        <span className="block truncate" title={template.name}>{template.name}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {getCategoryBadge(template.category)}
                          {template.category_changed_by_meta && (
                            <span title={`Submitted as ${template.requested_category}, changed by Meta`}>
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(template.status)}</TableCell>
                      <TableCell>
                        {template.header_type ? (
                          <div className="flex items-center gap-1 text-[#595C5C]">
                            {getHeaderIcon(template.header_type)}
                            <span className="text-[11px]">{template.header_type}</span>
                          </div>
                        ) : (
                          <span className="text-[11px] text-[#AFB0B0]">None</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-[#595C5C]">{template.buttons?.length || 0}</span>
                      </TableCell>
                      <TableCell className="text-[#595C5C]">{template.language}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedTemplate(template); setIsViewOpen(true); }}
                            className="h-8 w-8 p-0 text-[#595C5C] hover:text-[#131517]"
                            data-testid={`view-template-${template.id}`}
                            title="Preview template"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (!canEdit(template)) {
                                toast.warning('Cannot edit: template is pending Meta approval');
                                return;
                              }
                              setSelectedTemplate(template);
                              setIsEditOpen(true);
                            }}
                            className={`h-8 w-8 p-0 ${canEdit(template) ? 'text-[#595C5C] hover:text-[#131517]' : 'text-[#D9E2E5] cursor-not-allowed'}`}
                            data-testid={`edit-template-${template.id}`}
                            title={getEditTooltip(template)}
                          >
                            {template.status === 'PENDING' ? <Lock className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                          </Button>
                          {template.status === 'DRAFT' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSubmit(template.id)}
                              className="h-8 w-8 p-0 text-[#3BAB59] hover:text-[#3BAB59] hover:bg-[#3BAB59]/10"
                              data-testid={`submit-template-${template.id}`}
                              title="Submit for approval"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(template.id)}
                            className="h-8 w-8 p-0 text-[#EB493D] hover:text-[#EB493D] hover:bg-[#EB493D]/10"
                            data-testid={`delete-template-${template.id}`}
                            title="Delete template"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination Bar */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#D9E2E5]">
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-[#8A8A8B]">Rows per page:</span>
                  <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
                    <SelectTrigger className="h-7 w-16 text-[12px] border-[#D9E2E5]" data-testid="page-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-[12px] text-[#8A8A8B]">
                    {((safePage - 1) * pageSize) + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      className="h-7 w-7 p-0 border-[#D9E2E5]"
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </Button>
                    {/* Page number pills */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let page;
                      if (totalPages <= 5) {
                        page = i + 1;
                      } else if (safePage <= 3) {
                        page = i + 1;
                      } else if (safePage >= totalPages - 2) {
                        page = totalPages - 4 + i;
                      } else {
                        page = safePage - 2 + i;
                      }
                      return (
                        <Button
                          key={page}
                          variant={page === safePage ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className={`h-7 w-7 p-0 text-[12px] ${page === safePage ? 'bg-[#131517] border-[#131517]' : 'border-[#D9E2E5]'}`}
                          data-testid={`page-btn-${page}`}
                        >
                          {page}
                        </Button>
                      );
                    })}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={safePage === totalPages}
                      className="h-7 w-7 p-0 border-[#D9E2E5]"
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-[#D9E2E5] mb-3" />
              {hasFilters ? (
                <>
                  <p className="text-[14px] text-[#8A8A8B]">No templates match your filters</p>
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="mt-2 text-[#595C5C]">Clear filters</Button>
                </>
              ) : (
                <>
                  <p className="text-[14px] text-[#8A8A8B]">No templates yet</p>
                  <p className="text-[12px] text-[#AFB0B0] mt-1">Create your first template to get started</p>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Template Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-[20px] font-semibold text-[#131517]">Template Preview</DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <div className="grid grid-cols-2 gap-6 pt-4 overflow-y-auto pr-1">
              {/* Details */}
              <div className="space-y-4">
                <div className="flex gap-2 flex-wrap">
                  {getStatusBadge(selectedTemplate.status)}
                  {getCategoryBadge(selectedTemplate.category)}
                </div>
                <div>
                  <p className="text-[12px] font-semibold text-[#8A8A8B] uppercase mb-1">Name</p>
                  <p className="text-[14px] text-[#131517] break-all">{selectedTemplate.name}</p>
                </div>
                <div>
                  <p className="text-[12px] font-semibold text-[#8A8A8B] uppercase mb-1">Language</p>
                  <p className="text-[14px] text-[#131517]">{selectedTemplate.language}</p>
                </div>
                {selectedTemplate.header_type && (
                  <div>
                    <p className="text-[12px] font-semibold text-[#8A8A8B] uppercase mb-1">Header Type</p>
                    <div className="flex items-center gap-2">
                      {getHeaderIcon(selectedTemplate.header_type)}
                      <span className="text-[14px] text-[#131517]">{selectedTemplate.header_type}</span>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-[12px] font-semibold text-[#8A8A8B] uppercase mb-1">Body</p>
                  <p className="text-[13px] text-[#131517] whitespace-pre-wrap bg-[#EFEFEF] p-3 rounded-lg max-h-[200px] overflow-y-auto">
                    {selectedTemplate.body_text}
                  </p>
                </div>
                {selectedTemplate.footer_text && (
                  <div>
                    <p className="text-[12px] font-semibold text-[#8A8A8B] uppercase mb-1">Footer</p>
                    <p className="text-[13px] text-[#595C5C]">{selectedTemplate.footer_text}</p>
                  </div>
                )}
                {selectedTemplate.buttons && selectedTemplate.buttons.length > 0 && (
                  <div>
                    <p className="text-[12px] font-semibold text-[#8A8A8B] uppercase mb-2">Buttons ({selectedTemplate.buttons.length})</p>
                    <div className="space-y-2">
                      {selectedTemplate.buttons.map((btn, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-[13px] text-[#131517] bg-[#EFEFEF] px-3 py-2 rounded">
                          {btn.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4 text-[#00A884]" />}
                          {btn.type === 'URL' && <ExternalLink className="w-4 h-4 text-[#00A884]" />}
                          {btn.type === 'QUICK_REPLY' && <MessageSquare className="w-4 h-4 text-[#00A884]" />}
                          {btn.type === 'COPY_CODE' && <Copy className="w-4 h-4 text-[#00A884]" />}
                          <span>{btn.text}</span>
                          <span className="text-[11px] text-[#8A8A8B] ml-auto">{btn.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Preview */}
              <div>
                <p className="text-[12px] font-semibold text-[#8A8A8B] uppercase mb-3">WhatsApp Preview</p>
                <TemplatePreview template={selectedTemplate} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={isEditOpen} onOpenChange={(open) => { setIsEditOpen(open); if (!open) setSelectedTemplate(null); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[20px] font-semibold text-[#131517]">
              Edit Template
              {selectedTemplate?.status === 'APPROVED' && (
                <span className="ml-3 text-[12px] font-normal text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                  Approved — max 1 edit per 24 hours
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <div className="pt-4">
              <TemplateBuilder
                key={selectedTemplate.id}
                initialData={selectedTemplate}
                onSave={handleEdit}
                onCancel={() => { setIsEditOpen(false); setSelectedTemplate(null); }}
                saving={saving}
                isEditMode={true}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default TemplatesPage;
